/* SearchAnalyticsWidget — admin Overview tile.
 *
 * Surfaces the popular and unhelpful search queries logged by the unified
 * search endpoint (/api/search). Two tabs:
 *  - Top searches: ranked by query count, with avg results, click-through
 *    rate, and the most-clicked target slug per query
 *  - Catalog gaps: queries that returned ZERO results — the priority list
 *    for new products, programs, or services to add
 *
 * Period selector: 7 / 30 / 90 days. Empty-state designed for first-time
 * admins (no events yet). Reads cookie auth (matches the rest of /admin).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Search, TrendingUp, AlertTriangle, Users, Eye, MousePointerClick, RefreshCw, ExternalLink } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const KIND_LABEL = {
  systems_service: { label: 'Systems', tint: 'bg-[#5b8def]/10 text-[#3a6fcc]' },
  services_product: { label: 'Services', tint: 'bg-[#c9a227]/10 text-[#a8871a]' },
  academy_program: { label: 'Academy', tint: 'bg-[#a875ff]/10 text-[#7e4ad6]' },
};
const KIND_URL = {
  systems_service: (slug) => `/systems/solutions/${slug}`,
  services_product: (slug) => `/services/p/${slug}`,
  academy_program: (slug) => `/academy/programs/${slug}`,
};

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function SearchAnalyticsWidget() {
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState('top');
  const [topRows, setTopRows] = useState([]);
  const [zeroRows, setZeroRows] = useState([]);
  const [totals, setTotals] = useState({ total_searches: 0, unique_queries: 0, unique_ips: 0, zero_count: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, zRes] = await Promise.all([
        fetch(`${API}/admin/search/top-queries?days=${days}&limit=10`, { credentials: 'include' }),
        fetch(`${API}/admin/search/zero-results?days=${days}&limit=10`, { credentials: 'include' }),
      ]);
      if (tRes.ok) {
        const d = await tRes.json();
        setTopRows(d.rows || []);
        setTotals(d.totals || { total_searches: 0, unique_queries: 0, unique_ips: 0, zero_count: 0 });
      }
      if (zRes.ok) {
        const d = await zRes.json();
        setZeroRows(d.rows || []);
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const maxCount = Math.max(1, ...topRows.map((r) => r.count || 0));
  const empty = !loading && totals.total_searches === 0;

  return (
    <section
      data-testid="admin-search-analytics-widget"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-[#f8f8fa] border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Search Analytics</div>
            <h3 className="text-[15px] font-bold text-gray-900">What visitors are looking for</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`search-analytics-period-${d}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                days === d ? 'bg-[#c9a227] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#c9a227]'
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="search-analytics-refresh"
            disabled={loading}
            className="p-1.5 rounded-full border border-gray-200 text-gray-500 hover:text-[#c9a227] hover:border-[#c9a227] transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {/* Stats strip */}
      <div className="px-6 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-gray-100">
        <Stat icon={Search} label="Searches" value={totals.total_searches} />
        <Stat icon={TrendingUp} label="Unique queries" value={totals.unique_queries} />
        <Stat icon={Users} label="Unique visitors" value={totals.unique_ips} />
        <Stat icon={AlertTriangle} label="Zero-result hits" value={totals.zero_count} accent={totals.zero_count > 0 ? 'text-rose-600' : 'text-gray-700'} />
      </div>

      {/* Tab switcher */}
      <div className="px-6 pt-3 flex items-center gap-1.5">
        <TabBtn active={tab === 'top'} onClick={() => setTab('top')} testid="search-analytics-tab-top" icon={TrendingUp}>
          Top searches <span className="text-gray-400 ml-1">{topRows.length}</span>
        </TabBtn>
        <TabBtn active={tab === 'zero'} onClick={() => setTab('zero')} testid="search-analytics-tab-zero" icon={AlertTriangle} danger={zeroRows.length > 0}>
          Catalog gaps <span className="ml-1" style={{ color: zeroRows.length > 0 ? '#e11d48' : '#9ca3af' }}>{zeroRows.length}</span>
        </TabBtn>
      </div>

      {/* Body */}
      <div className="px-6 py-4">
        {empty && (
          <div className="text-center py-8" data-testid="search-analytics-empty">
            <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-[13px] text-gray-500">No searches yet in this window.</p>
            <p className="text-[11.5px] text-gray-400 mt-1">As visitors use ⌘K search, their queries land here.</p>
          </div>
        )}

        {!empty && tab === 'top' && (
          <ul className="flex flex-col gap-2" data-testid="search-analytics-top-list">
            {topRows.map((r, i) => {
              const tc = r.top_click;
              const meta = tc && KIND_LABEL[tc.kind];
              const url = tc && KIND_URL[tc.kind] ? KIND_URL[tc.kind](tc.slug) : null;
              return (
                <li key={r.q_lower} className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-gray-50 transition-colors">
                  <span className="text-[10.5px] font-bold text-gray-400 w-5 text-right">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13.5px] font-semibold text-gray-900 truncate">"{r.q}"</span>
                    <span className="block mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <span className="block h-full rounded-full" style={{ width: `${(r.count / maxCount) * 100}%`, background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }} />
                    </span>
                    <span className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                      <span><Eye className="w-3 h-3 inline mr-1" />{r.count} {r.count === 1 ? 'search' : 'searches'}</span>
                      <span>· avg {Math.round(r.avg_results || 0)} results</span>
                      <span>· {timeAgo(r.last_ts)}</span>
                      {r.zero_count > 0 && <span className="text-rose-600">· {r.zero_count} empty</span>}
                    </span>
                    {tc && meta && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] text-gray-600 hover:text-[#c9a227]"
                      >
                        <span className={`px-1.5 py-0.5 rounded uppercase tracking-wider text-[9.5px] font-bold ${meta.tint}`}>{meta.label}</span>
                        <span className="truncate max-w-[200px]">{tc.slug}</span>
                        <span className="text-gray-400">· {tc.n} click{tc.n === 1 ? '' : 's'}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block text-[14px] font-bold text-gray-900">{r.ctr || 0}%</span>
                    <span className="block text-[10px] uppercase tracking-wider text-gray-400 inline-flex items-center gap-1"><MousePointerClick className="w-2.5 h-2.5" />CTR</span>
                  </span>
                </li>
              );
            })}
            {topRows.length === 0 && !loading && (
              <li className="text-center py-6 text-[12px] text-gray-400">No searches in the last {days} days.</li>
            )}
          </ul>
        )}

        {!empty && tab === 'zero' && (
          <ul className="flex flex-col gap-1.5" data-testid="search-analytics-zero-list">
            {zeroRows.map((r) => (
              <li key={r.q_lower} className="flex items-center gap-3 py-2 px-2 rounded-md border border-rose-100 bg-rose-50/50">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13.5px] font-semibold text-gray-900 truncate">"{r.q}"</span>
                  <span className="block text-[11px] text-gray-500">{r.count} hit{r.count === 1 ? '' : 's'} · {r.unique_visitors} visitor{r.unique_visitors === 1 ? '' : 's'} · {timeAgo(r.last_ts)}</span>
                </span>
                <span className="text-[10.5px] uppercase tracking-wider text-rose-600 font-bold">No results</span>
              </li>
            ))}
            {zeroRows.length === 0 && !loading && (
              <li className="text-center py-6 text-[12px] text-emerald-700">
                No catalog gaps in the last {days} days. Every search returned at least one result.
              </li>
            )}
            {zeroRows.length > 0 && (
              <li className="px-2 pt-2 text-[11px] text-gray-500">
                Tip: each row is a query visitors typed but found NOTHING for. Add matching products, services, or programs to capture this demand.
              </li>
            )}
          </ul>
        )}
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, accent = 'text-gray-900' }) {
  return (
    <div className="flex items-start gap-2">
      <span className="h-8 w-8 rounded-md bg-[#c9a227]/10 grid place-items-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-[#c9a227]" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className={`text-[16px] font-bold ${accent}`}>{value}</span>
        <span className="text-[10.5px] uppercase tracking-wider text-gray-500">{label}</span>
      </span>
    </div>
  );
}

function TabBtn({ active, onClick, children, icon: Icon, testid, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold transition-colors ${
        active
          ? danger
            ? 'bg-rose-50 text-rose-700 border border-rose-200'
            : 'bg-[#c9a227] text-white'
          : 'text-gray-500 hover:bg-gray-100'
      }`}
    >
      <Icon className="w-3 h-3" />
      {children}
    </button>
  );
}
